import { useEffect, useState, useSyncExternalStore } from "react";

export type Breakpoint = "mobile" | "tablet" | "desktop";

export interface AppShell {
  /** Phones in the browser (< 768px) or the installed app below desktop width: no footer, tab bar instead. */
  isMobileShell: boolean;
  /** Running as the installed PWA (Android/desktop `display-mode: standalone`, or iOS home-screen). */
  isStandalone: boolean;
  breakpoint: Breakpoint;
}

const MOBILE = "(max-width: 767px)";
const DESKTOP = "(min-width: 1024px)";
const STANDALONE = "(display-mode: standalone)";

function matches(query: string, fallback: boolean): boolean {
  return typeof window !== "undefined" && window.matchMedia ? window.matchMedia(query).matches : fallback;
}

function iosStandalone(): boolean {
  return typeof navigator !== "undefined" && (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

/** The current shell, computed from media queries (no React state). Mirrors the CSS rules in global.css. */
export function readAppShell(): AppShell {
  const isStandalone = matches(STANDALONE, false) || iosStandalone();
  const desktop = matches(DESKTOP, true);
  const breakpoint: Breakpoint = desktop ? "desktop" : matches(MOBILE, false) ? "mobile" : "tablet";
  return { isStandalone, breakpoint, isMobileShell: breakpoint === "mobile" || (isStandalone && !desktop) };
}

function subscribe(onChange: () => void): () => void {
  if (typeof window === "undefined" || !window.matchMedia) return () => undefined;
  const queries = [MOBILE, DESKTOP, STANDALONE].map((q) => window.matchMedia(q));
  queries.forEach((q) => q.addEventListener?.("change", onChange));
  return () => queries.forEach((q) => q.removeEventListener?.("change", onChange));
}

let cached: AppShell | null = null;
function snapshot(): AppShell {
  const next = readAppShell();
  if (
    !cached ||
    cached.isMobileShell !== next.isMobileShell ||
    cached.isStandalone !== next.isStandalone ||
    cached.breakpoint !== next.breakpoint
  ) {
    cached = next;
  }
  return cached;
}

/**
 * One source of truth for "is this the phone app shell?" (Jumia/Jiji-style: no website footer,
 * bottom tab bar). CSS hides the footer for the same cases before React runs, so it never flashes.
 */
export function useAppShell(): AppShell {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}

/** True while the on-screen keyboard is up: the visual viewport is > 150px shorter than the layout viewport. */
export function useKeyboardOpen(): boolean {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const vv = typeof window !== "undefined" ? window.visualViewport : null;
    if (!vv) return;
    let frame = 0;
    const update = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setOpen(window.innerHeight - vv.height > 150));
    };
    update();
    vv.addEventListener("resize", update);
    return () => {
      cancelAnimationFrame(frame);
      vv.removeEventListener("resize", update);
    };
  }, []);
  return open;
}
